import { usePushNotifications } from '@common/push-notifications-layer/composables/usePushNotifications'
import { defineNuxtPlugin } from 'nuxt/app'

/**
 * Firebase Cloud Messaging plugin (client-only)
 *
 * Runs after authentication is established (see plugin order in nuxt.base.config.ts).
 * Responsibilities:
 *  1. Register the SW → app message bridge immediately (before any notification click)
 *  2. Wire auth-gated token registration via setupAuthenticatedPushRegistration — permission
 *     and backend register-push-token only run when the user is logged in with sessionUniqueKey
 *  3. Set up a foreground message handler so notifications show while the app is open
 *
 * Public/anonymous routes still load this plugin; only token registration is auth-gated.
 * SW bridge and foreground handler are intentionally always wired without permission.
 *
 * Opening a community chat conversation from a notification is NOT handled here — the SW
 * bridge / cold-start URL land the user on the conversation route, and the
 * `conversation-deep-link` middleware + client plugin (communities routing-layer) take over
 * (desktop floating window vs mobile route). Keeps this plugin domain-free.
 */
export default defineNuxtPlugin(() => {
  const pushNotifications = usePushNotifications()
  if (!pushNotifications.isFirebaseMessagingConfigured()) {
    return
  }

  const { setupAuthenticatedPushRegistration, setupForegroundMessageHandler, setupSWMessageListener, disposePushNotificationSideEffects } =
    pushNotifications

  // Must be wired up first — a notification click could arrive before the async
  // permission/token flow completes
  const stopSwBridge = setupSWMessageListener()
  const stopAuthRegistration = setupAuthenticatedPushRegistration()

  const teardownFcmPluginSideEffects = () => {
    stopSwBridge()
    stopAuthRegistration()
    disposePushNotificationSideEffects()
  }

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      teardownFcmPluginSideEffects()
    })
  }

  // Foreground handler runs in the background so the plugin does not block rendering.
  void (async () => {
    try {
      await setupForegroundMessageHandler()
    } catch (err) {
      console.error('[firebase-messaging] Foreground handler failed:', err)
    }
  })()
})
